// Reusable media upload control (PX-248). Drag-n-drop OR click to upload a
// file to POST /admin/media/upload (PX-247), which validates magic bytes,
// stores the bytes on the self-hosted /media volume, and returns a permanent
// URL. The URL — never the bytes — is what the parent form persists. A
// "paste a URL" fallback stays for externally-hosted assets. Reused by the
// cover field, variant examples, and the login/paywall/featured carousels.

import { useId, useRef, useState } from 'react'
import { APIError, uploadFile } from '@/lib/api'
import type { MediaUploadResponse } from '@/types/api'

const VIDEO_EXT = /\.(mp4|mov|m4v)(\?|$)/i

export function MediaDropzone({
  value,
  onChange,
  onCommit,
  accept = 'image',
  large = false,
  disabled,
}: {
  value: string
  onChange: (url: string) => void
  /**
   * Fired on DISCRETE commits — a successful upload, a Clear, or the
   * paste-URL field losing focus — but NOT on every keystroke. Lets a
   * caller persist (e.g. PATCH a row) without a request per typed char.
   * When omitted the control is purely local (collect-then-submit usage).
   */
  onCommit?: (url: string) => void
  /** 'image' = images only; 'media' = images + short video. */
  accept?: 'image' | 'media'
  /** Bigger drop target — used for the prominent "add new model" zone. */
  large?: boolean
  disabled?: boolean
}) {
  const inputId = useId()
  const inputRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [dragOver, setDragOver] = useState(false)

  const acceptAttr = accept === 'media' ? 'image/*,video/*' : 'image/*'

  async function handleFile(file: File | undefined | null) {
    if (!file || disabled || uploading) return
    setError(null)
    setUploading(true)
    try {
      const res = await uploadFile<MediaUploadResponse>('/admin/media/upload', file)
      onChange(res.url)
      onCommit?.(res.url)
    } catch (e) {
      setError(e instanceof APIError ? e.message : 'Upload failed')
    } finally {
      setUploading(false)
      if (inputRef.current) inputRef.current.value = '' // allow re-selecting the same file
    }
  }

  const isVideo = !!value && VIDEO_EXT.test(value)

  return (
    <div className="space-y-2">
      <div
        onDragOver={(e) => {
          e.preventDefault()
          if (!disabled) setDragOver(true)
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault()
          setDragOver(false)
          void handleFile(e.dataTransfer.files?.[0])
        }}
        onClick={() => !disabled && !uploading && inputRef.current?.click()}
        className={`flex cursor-pointer flex-col items-center justify-center gap-1 rounded-md border-2 border-dashed text-center text-xs transition ${
          large ? 'px-4 py-9' : 'px-3 py-4'
        } ${dragOver ? 'border-brand-500 bg-brand-50' : 'border-ink-200 hover:border-ink-300'} ${
          disabled ? 'cursor-not-allowed opacity-60' : ''
        }`}
      >
        <input
          ref={inputRef}
          id={inputId}
          type="file"
          accept={acceptAttr}
          hidden
          disabled={disabled}
          onChange={(e) => void handleFile(e.target.files?.[0])}
        />
        {uploading ? (
          <span className="text-ink-500">Uploading…</span>
        ) : (
          <>
            <span className="font-medium text-ink-600">Drop a file or click to upload</span>
            <span className="text-ink-400">{accept === 'media' ? 'Image or short video' : 'Image'}</span>
          </>
        )}
      </div>

      {error && <p className="text-xs text-red-600">{error}</p>}

      {/* Paste-URL fallback for externally-hosted assets. */}
      <input
        type="url"
        value={value}
        disabled={disabled}
        placeholder="or paste a URL"
        onChange={(e) => onChange(e.target.value)}
        onBlur={(e) => onCommit?.(e.target.value.trim())}
        className="w-full rounded-md border border-ink-200 px-3 py-2 font-mono text-xs focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100"
      />

      {value && (
        <div className="flex items-center gap-2">
          {isVideo ? (
            <video src={value} className="h-24 w-24 rounded-md object-cover" muted />
          ) : (
            <img src={value} alt="preview" className="h-24 w-24 rounded-md object-cover" />
          )}
          <button
            type="button"
            disabled={disabled}
            onClick={() => { onChange(''); onCommit?.('') }}
            className="rounded-md border border-ink-200 px-2 py-1 text-xs text-ink-600 transition hover:bg-ink-50 disabled:opacity-50"
          >
            Clear
          </button>
        </div>
      )}
    </div>
  )
}
